import { UpdateVisitorPresenceMutation, UpdateVisitorPresenceMutationVariables } from '../../generated/graphql';

import { gql, useMutation } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { logger } from '../../datadog/logger';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { useAdmissionFlowCallback } from '../../hooks/useAdmissionFlow';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { VisitorState, activeConversationSidAtom, visitorNameAtom, visitorStateAtom } from '../state';
import { useVisitorId } from '../useVisitorId';
import { admissionTimeAtom } from './admission/state';

const updateVisitorPresenceMutationGql = gql`
  mutation UpdateVisitorPresence(
    $providerShortId: String!
    $patientShortId: String!
    $appointmentShortId: String
    $visitorUuid: String!
    $visitorName: String!
    $waitingRoomConversationSid: String
    $videoPlatform: String
    $joinRoomMode: String
    $admissionTime: String
    $isInForeground: Boolean
    $isJoining: Boolean
  ) {
    updateTwilioVisitorPresence(
      providerShortId: $providerShortId
      patientShortId: $patientShortId
      appointmentShortId: $appointmentShortId
      visitorUuid: $visitorUuid
      visitorName: $visitorName
      waitingRoomConversationSid: $waitingRoomConversationSid
      videoPlatform: $videoPlatform
      joinRoomMode: $joinRoomMode
      admissionTime: $admissionTime
      isInForeground: $isInForeground
      isJoining: $isJoining
    ) {
      updatedAt
    }
  }
`;

export function useUpdateVisitorPresenceMutation() {
  return useMutation<UpdateVisitorPresenceMutation, UpdateVisitorPresenceMutationVariables>(
    updateVisitorPresenceMutationGql,
  );
}

export function useUpdateVisitorPresenceCallback() {
  const visitorUuid = useVisitorId();
  const [updateVisitorPresence] = useUpdateVisitorPresenceMutation();
  const getVideoPlatform = useVideoPlatformCallback();
  const getAdmissionFlow = useAdmissionFlowCallback();
  const { providerShortId, patientShortId } = useParams() as {
    providerShortId: string;
    patientShortId: string;
  };
  const visitorName = useAtomValue(visitorNameAtom);
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const getActiveConversationSid = useAtomCallbackMemoized((get) => get(activeConversationSidAtom), []);
  const getAdmissionTime = useAtomCallbackMemoized((get) => get(admissionTimeAtom), []);
  const getVisitorState = useAtomCallbackMemoized((get) => get(visitorStateAtom), []);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;

  return useCallback(
    async () => {
      const activeConversationSid = getActiveConversationSid();
      const isInForeground = typeof document === 'undefined' ? true : document.visibilityState === 'visible';
      const isJoining = getVisitorState() === VisitorState.JOINING;
      try {
        await updateVisitorPresence({
          variables: {
            providerShortId,
            patientShortId,
            appointmentShortId,
            visitorUuid,
            visitorName,
            joinRoomMode: getAdmissionFlow(),
            admissionTime: getAdmissionTime(),
            isInForeground,
            isJoining,
            ...(activeConversationSid && {
              waitingRoomConversationSid: activeConversationSid,
            }),
            videoPlatform: getVideoPlatform(),
          },
          context: {
            skipAuth: true,
          },
        });
      } catch (e) {
        const error = e as Error;
        logger.error('Unable to update visitor presence', {}, error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [providerShortId, patientShortId, appointmentShortId, visitorUuid, visitorName, getVideoPlatform, getAdmissionFlow],
  );
}
